import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChart3, Clock, PauseCircle, TrendingUp } from 'lucide-react';
import type { OpsKaizenData } from './useOpsKaizenData';
import { OpsPrimaryBottleneckCard } from '../executive/OpsPrimaryBottleneckCard';
import { stageIsFocused } from '../exceptionLoop';
import { EXEC_HERO_CLASS, EXEC_SECTION_LABEL } from '../executive/opsExecutiveShared';
import {
  ACCENT_BLUE,
  ACCENT_BLUE_SOFT,
  CHART_ALERT,
  CHART_BAR,
  CHART_GRID,
  CHART_TICK,
  KAIZEN_GAP,
  KAIZEN_QUAD_GRID,
  KAIZEN_VIEWPORT_CLASS,
  RECHART_MARGIN,
  ChartBlock,
  PremiumTooltip,
} from './opsKaizenShared';

interface OpsKaizenLossesTabProps {
  data: OpsKaizenData;
  focusStage?: string | null;
  focusWip?: number | null;
  focusMinutes?: number | null;
  onRegisterProblem?: () => void;
  onDismissFocus?: () => void;
}

const BOTTLENECK_INDEX_FORMULA = 'Índice de priorização operacional baseado na combinação entre acúmulo de trabalho (WIP) e desempenho de SLA da etapa. Fórmula: WIP × max(100 − SLA, 1). Não é economia nem impacto financeiro.';

export const OpsKaizenLossesTab: React.FC<OpsKaizenLossesTabProps> = ({
  data,
  focusStage = null,
  focusWip = null,
  focusMinutes = null,
  onRegisterProblem,
  onDismissFocus,
}) => {
  const [showIndex, setShowIndex] = useState(false);
  const rankedBottlenecks = data.bottleneckChartData
    .filter((b) => b.stage_name !== '—' && b.recurrence_score > 0)
    .slice(0, 5);

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${KAIZEN_GAP} ${KAIZEN_VIEWPORT_CLASS}`}>
      {focusStage ? (
        <div className="shrink-0 rounded-sm border border-sky-500/40 bg-sky-500/[0.06] px-4 py-2.5">
          <p className="text-[12px] font-semibold text-[var(--text-primary)]">
            Você chegou aqui por causa do gargalo {focusStage}.
          </p>
          <p className="mt-0.5 text-[10px] text-[var(--text-secondary)]">
            {focusWip != null ? `WIP ${focusWip}` : 'WIP não informado nesta navegação'}
            {focusMinutes != null ? ` · ${focusMinutes} min parados em média` : ''}
            {' · '}Sinal operacional: Gargalo
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {onRegisterProblem ? (
              <button
                type="button"
                onClick={onRegisterProblem}
                className="text-[11px] font-semibold text-[var(--nav-active-bar)]"
              >
                Registrar problema
              </button>
            ) : null}
            {onDismissFocus ? (
              <button
                type="button"
                onClick={onDismissFocus}
                className="text-[10px] text-[var(--text-muted)]"
              >
                Limpar foco
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      <OpsPrimaryBottleneckCard bottleneck={data.primaryBottleneck} />

      {rankedBottlenecks.length > 0 && (
        <div className={`${EXEC_HERO_CLASS} shrink-0`}>
          <div className="flex items-center justify-between gap-2 border-b border-[var(--border-default)]/50 px-4 py-2">
            <p className={EXEC_SECTION_LABEL}>Top Gargalos Recorrentes</p>
            <button
              type="button"
              onClick={() => setShowIndex((open) => !open)}
              className="text-[10px] text-[var(--text-muted)] underline-offset-2 hover:text-[var(--text-primary)] hover:underline"
            >
              Como é calculado?
            </button>
          </div>
          {showIndex && (
            <p className="border-b border-[var(--border-default)]/40 px-4 py-2 text-[10px] leading-relaxed text-[var(--text-secondary)]">
              {BOTTLENECK_INDEX_FORMULA}
            </p>
          )}
          <ul className="divide-y divide-[var(--border-default)]/40">
            {rankedBottlenecks.map((bn, i) => {
              const isPrimary = data.primaryBottleneck?.stageName === bn.stage_name;
              const focused = stageIsFocused(bn.stage_name, focusStage);
              return (
                <li
                  key={bn.stage_name}
                  className={`flex items-center gap-3 px-4 py-2.5 ${focused ? 'bg-sky-500/10 ring-1 ring-inset ring-sky-500/40' : ''}`}
                >
                  <span className="w-5 shrink-0 text-[12px] font-bold tabular-nums text-[var(--text-muted)]">{i + 1}º</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-[var(--text-primary)]">{bn.stage_name}</p>
                    <p className="text-[10px] text-[var(--text-secondary)]">
                      WIP {Math.round(bn.avg_wip)} · SLA {bn.sla_rate.toFixed(0)}%
                      {isPrimary ? ' · Gargalo principal' : ''}
                      {focused ? ' · Etapa em foco' : ''}
                    </p>
                  </div>
                  <span
                    className="shrink-0 text-right text-[10px] text-[var(--text-muted)]"
                    title={BOTTLENECK_INDEX_FORMULA}
                  >
                    Índice de gargalo
                    <span className="mt-0.5 block font-semibold tabular-nums text-[var(--text-secondary)]">
                      {bn.recurrence_score.toFixed(0)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className={`min-h-0 flex-1 ${KAIZEN_QUAD_GRID}`}>
        <ChartBlock
          title="Tendência de SLA"
          icon={<Clock size={13} className="text-sky-600" strokeWidth={2.25} />}
          subtitle={`${data.chartDays}d`}
          hasActivity={data.hasSlaTrendData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.slaChartData} margin={RECHART_MARGIN.line}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.chartDays / 7) - 1)} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} tickFormatter={(v) => `${v}%`} width={36} />
              <ReferenceLine y={100} stroke="#64748B" strokeDasharray="4 4" />
              <Tooltip cursor={{ stroke: '#31475f', strokeWidth: 1 }} content={<PremiumTooltip formatter={(value, payload) => ({ main: `${value.toFixed(1)}%`, sub: `${payload?.total || 0} concluídas` })} />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={ACCENT_BLUE}
                strokeWidth={1.5}
                dot={(props) => {
                  const pointValue = Number(props.payload?.value ?? 0);
                  if (pointValue >= 100) return <circle cx={props.cx} cy={props.cy} r={1.5} fill={ACCENT_BLUE_SOFT} />;
                  return <circle cx={props.cx} cy={props.cy} r={2.5} fill={CHART_ALERT} stroke="var(--bg-surface)" strokeWidth={1} />;
                }}
                activeDot={{ r: 3, fill: ACCENT_BLUE_SOFT, stroke: 'var(--bg-surface)', strokeWidth: 1 }}
                isAnimationActive
                animationDuration={1200}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock
          title="Throughput por Dia"
          icon={<TrendingUp size={13} className="text-indigo-600" strokeWidth={2.25} />}
          subtitle="Tarefas concluídas"
          hasActivity={data.hasThroughputData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.throughputChartData} margin={RECHART_MARGIN.bar}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} interval={Math.max(0, Math.floor(data.chartDays / 7) - 1)} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} width={24} />
              <Tooltip cursor={{ fill: 'rgba(95, 127, 162, 0.08)' }} content={<PremiumTooltip formatter={(value) => ({ main: `${value} tarefas`, sub: '' })} />} />
              <Bar dataKey="value" fill={CHART_BAR} radius={[2, 2, 0, 0]} isAnimationActive animationDuration={1200} />
            </BarChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock
          title="Gargalo Recorrente"
          icon={<BarChart3 size={13} className="text-amber-600" strokeWidth={2.25} />}
          subtitle="Índice = WIP × max(100 − SLA, 1)"
          hasActivity={data.hasBottleneckData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.bottleneckChartData} layout="vertical" margin={RECHART_MARGIN.barVertical}>
              <CartesianGrid horizontal={false} vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis type="number" axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <YAxis type="category" dataKey="stage_name" axisLine={false} tickLine={false} width={96} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <Tooltip content={<PremiumTooltip formatter={(value, payload) => ({ main: `Índice de gargalo ${Number(value).toFixed(0)}`, sub: `WIP ${payload?.avg_wip ?? 0} · ${BOTTLENECK_INDEX_FORMULA}` })} />} />
              <Bar dataKey="va" stackId="a" fill="#10B981" isAnimationActive animationDuration={1200} />
              <Bar dataKey="nva" stackId="a" fill="#1E293B" radius={[0, 8, 8, 0]} isAnimationActive animationDuration={1200}>
                <LabelList dataKey="recurrence_score" position="right" offset={6} className="fill-[var(--text-secondary)]" fontSize={10} formatter={(v: unknown) => Number(v).toFixed(0)} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartBlock>

        <ChartBlock
          title="Motivos de Pausa (NVA)"
          icon={<PauseCircle size={13} className="text-amber-600" strokeWidth={2.25} />}
          subtitle="Pareto acumulado"
          hasActivity={data.hasPauseData}
          fill
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.pauseChartData} margin={RECHART_MARGIN.composed}>
              <CartesianGrid vertical={false} stroke={CHART_GRID} strokeDasharray="3 3" />
              <XAxis dataKey="reason" axisLine={false} tickLine={false} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} width={28} tick={{ fill: CHART_TICK, fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} width={32} tick={{ fill: CHART_ALERT, fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                content={
                  <PremiumTooltip
                    formatter={(value, payload) =>
                      payload?.dataKey === 'count'
                        ? { main: `${value} paradas`, sub: 'Frequência' }
                        : { main: `${Number(value).toFixed(1)}%`, sub: 'Acumulado' }
                    }
                  />
                }
              />
              <Bar yAxisId="left" dataKey="count" fill="rgba(245, 158, 11, 0.2)" stroke={CHART_ALERT} strokeWidth={1} radius={[4, 4, 0, 0]} isAnimationActive animationDuration={1200} />
              <Line yAxisId="right" type="monotone" dataKey="cumulativePercentage" stroke={CHART_ALERT} strokeWidth={2} dot={{ r: 3, fill: CHART_ALERT, strokeWidth: 0 }} activeDot={{ r: 5 }} isAnimationActive animationDuration={1200} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartBlock>
      </div>
    </div>
  );
};

export default OpsKaizenLossesTab;
